import * as React from "react"

import { cva } from "./cva.ts"
import { cubicBezier } from "./easings.ts"
import { Frame, Interval } from "./schedulers.ts"

// We add the popover attribute as TS gives us an error that the attribute is not valid.
// TS Error: Property 'popover' does not exist on type 'DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>'.
declare module "react" {
  interface HTMLAttributes<T> extends React.AriaAttributes, React.DOMAttributes<T> {
    popover?: string
  }
}

// #region Pixelation Component ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export const Pixelation = React.forwardRef<HTMLElement, PixelationProps>(
  ({ children, className, ...props }, ref) => {
    const popoverRef = React.useRef<HTMLSpanElement | null>(null)
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null)

    React.useEffect(() => {
      const popover = popoverRef.current
      const canvas = canvasRef.current

      if (popover) {
        popover.showPopover()
      }

      if (canvas) {
        const pixelatedCanvas = new PixelatedCanvas(canvas)

        pixelatedCanvas.start()

        return () => {
          pixelatedCanvas.clear()
        }
      }
    }, [])

    return (
      <span
        className="fixed size-full grid bg-black text-white"
        popover="manual"
        ref={popoverRef}
        {...props}
      >
        <canvas
          className="[grid-area:-1/-1/1/1] size-full overflow-hidden [image-rendering:pixelated]"
          ref={canvasRef}
        />
        <span className={classForPixelation({ className })} ref={ref}>
          {children}
        </span>
      </span>
    )
  },
)

Pixelation.displayName = "Pixelation"

export type PixelationProps = React.ClassAttributes<HTMLElement> & React.HTMLAttributes<HTMLElement>

export type PixelationCanvasProps = React.ClassAttributes<HTMLCanvasElement> &
  React.HTMLAttributes<HTMLCanvasElement>

// #endregion ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// #region Pixelation Constants ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const pixelSize = 0.05
const maxBrightness = 35
const animationTime = 1666
const transitionTime = 1000

const getRandomBrightness = () => Math.round(Math.random() * maxBrightness)
const getBrightnessAsRGB = (b: number) => `rgb(${b} ${b} ${b})`

const animateWithCubicBezier = (callback: (progress: number) => void, duration: number) => {
  const initTime = performance.now()
  const stopTime = initTime + duration
  const easing = cubicBezier(0.4, 0, 0.2, 1)

  const frame = new Frame((currentTime) => {
    const elapsed = Math.min(currentTime - initTime, duration)

    callback(easing(elapsed / duration))

    if (currentTime < stopTime) {
      frame.queue()
    }
  }, duration)

  return frame
}

// #endregion ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// #region Pixelated Canvas ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

class PixelatedCanvas {
  canvas!: HTMLCanvasElement
  context!: CanvasRenderingContext2D
  pixels!: number[]
  viewport!: VisualViewport
  drawing!: Interval
  interval!: Interval
  transitions!: Frame[]

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas
    this.context = canvas.getContext("2d")!
    this.viewport = canvas.ownerDocument.defaultView!.visualViewport!

    this.pixels = []
    this.transitions = []

    this.handleResize = this.handleResize.bind(this)

    this.interval = new Interval(this.updateCanvas.bind(this), animationTime)
  }

  start() {
    this.viewport.addEventListener("resize", this.handleResize)

    this.handleResize()
  }

  clear() {
    this.viewport.removeEventListener("resize", this.handleResize)

    this.interval.clear()

    for (const transition of this.transitions) {
      transition?.clear()
    }
  }

  handleResize() {
    const w = Math.ceil(1 / pixelSize)
    const h = Math.ceil((window.innerHeight / window.innerWidth) * w)
    const p = Array.from({ length: w * h }, getRandomBrightness)

    this.pixels.splice(0, this.pixels.length, ...p)

    this.canvas.width = w
    this.canvas.height = h

    this.fillRect(getBrightnessAsRGB(0), { x: 0, y: 0, w, h })

    for (const transition of this.transitions) {
      transition?.clear()
    }

    this.interval.start()
  }

  updateCanvas() {
    const { canvas, pixels } = this

    for (let i = 0; i < pixels.length; ++i) {
      const pixel = pixels[i]

      const brightness = {
        old: pixel,
        new: Math.random() >= 0.6 ? getRandomBrightness() : pixel,
      }

      const rect: Rect = {
        x: i % canvas.width,
        y: Math.floor(i / canvas.width),
        w: 1,
        h: 1,
      }

      if (brightness.old === brightness.new) {
        this.fillRect(getBrightnessAsRGB(brightness.new), rect)

        continue
      }

      this.transitions[i] = animateWithCubicBezier((progress) => {
        pixels[i] = Math.round(brightness.old + (brightness.new - brightness.old) * progress)

        this.fillRect(getBrightnessAsRGB(pixels[i]), rect)
      }, transitionTime).start()
    }
  }

  fillRect(fill: string, rect: Rect) {
    this.context.fillStyle = fill
    this.context.fillRect(rect.x, rect.y, rect.w, rect.h)
  }
}

// #endregion ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// #region Pixelated Typing ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface Rect {
  x: number
  y: number
  w: number
  h: number
}

// #endregion ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const classForPixelation = cva(["[grid-area:-1/-1/1/1] z-10 p-4"])
